import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface MessageTemplate {
  id: string;
  org_id: string;
  template_type: string;
  name: string;
  subject: string;
  body_html: string;
  variables_json: string[];
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AVAILABLE_VARIABLES = [
  { token: '{{candidate_name}}', label: 'Candidate Name', description: 'Full name of the candidate' },
  { token: '{{candidate_email}}', label: 'Candidate Email', description: 'Email address of the candidate' },
  { token: '{{job_title}}', label: 'Job Title', description: 'Title of the job position' },
  { token: '{{company_name}}', label: 'Company Name', description: 'Your organization name' },
  { token: '{{interview_link}}', label: 'AI Interview Link', description: 'Link to start the AI video interview' },
  { token: '{{f2f_date}}', label: 'Interview Date & Time', description: 'Scheduled date and time for F2F interview' },
  { token: '{{f2f_location}}', label: 'Interview Location', description: 'Location or meeting link for F2F interview' },
  { token: '{{f2f_notes}}', label: 'Interview Notes', description: 'Additional notes from the recruiter' },
  { token: '{{offer_link}}', label: 'Offer Link', description: 'Link to view and accept the job offer' },
  { token: '{{portal_link}}', label: 'Portal Link', description: 'Link to the candidate portal' },
];

export { AVAILABLE_VARIABLES };

export function useMessageTemplates() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;
  const { toast } = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['message-templates', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .order('template_type')
        .order('is_default', { ascending: false });
      if (error) throw error;
      return (data ?? []) as MessageTemplate[];
    },
    enabled: !!orgId,
  });

  const upsertTemplate = useMutation({
    mutationFn: async (template: Partial<MessageTemplate> & { org_id: string }) => {
      if (template.id) {
        const { error } = await supabase
          .from('message_templates')
          .update({
            name: template.name,
            subject: template.subject,
            body_html: template.body_html,
            template_type: template.template_type,
            is_default: template.is_default,
            is_active: template.is_active,
          })
          .eq('id', template.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('message_templates')
          .insert({
            org_id: template.org_id,
            name: template.name!,
            subject: template.subject!,
            body_html: template.body_html!,
            template_type: template.template_type!,
            is_default: template.is_default ?? false,
            is_active: template.is_active ?? true,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['message-templates'] });
      toast({ title: 'Template saved' });
    },
    onError: (e: Error) => {
      toast({ title: 'Failed to save template', description: e.message, variant: 'destructive' });
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('message_templates').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['message-templates'] });
      toast({ title: 'Template deleted' });
    },
    onError: () => {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    },
  });

  return {
    templates: query.data ?? [],
    isLoading: query.isLoading,
    upsertTemplate,
    deleteTemplate,
  };
}
